import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import useElementWidth from './useElementWidth';
import '../../assets/styles/LineChart.css';

/* Grafico de lineas.
 *
 * Hermano de MiniChart y DonutChart: sin Tailwind, los estilos viven en
 * LineChart.css y el color de cada serie viaja por --line-color.
 *
 * El ancho se mide con ResizeObserver en vez de estirar un viewBox fijo: con
 * un viewBox estirado el trazo se deforma en horizontal y los puntos salen
 * ovalados, y aqui las lineas son lo unico que hay que mirar.
 *
 * Al pasar el raton no se señala un punto suelto sino toda la columna: se
 * levanta una guia vertical y salen los puntos de todas las series a la vez,
 * que es lo que deja comparar como iba cada una en esa fecha.
 */

const PADDING_TOP = 14;
const PADDING_BOTTOM = 10;
const GRID_LINES = 4;

const LineChart = React.forwardRef(
  (
    {
      series,
      categories,
      title,
      unit = '',
      height = 150,
      animationDuration = 1.1,
      animationDelayPerSeries = 0.12,
      className = '',
      ...props
    },
    ref
  ) => {
    const [plotRef, width] = useElementWidth();
    const [activeIndex, setActiveIndex] = React.useState(null);
    const [activeSeries, setActiveSeries] = React.useState(null);
    const reduced = useReducedMotion() === true;

    // Un 10% de aire por arriba para que el pico no toque el borde.
    const scaleMax = React.useMemo(() => {
      const max = series.reduce(
        (top, one) => Math.max(top, ...one.values),
        0
      );
      return max === 0 ? 1 : max * 1.1;
    }, [series]);

    const innerHeight = height - PADDING_TOP - PADDING_BOTTOM;
    const stepX = categories.length > 1 ? width / (categories.length - 1) : 0;

    const pointAt = (value, index) => ({
      x: index * stepX,
      y: PADDING_TOP + innerHeight - (value / scaleMax) * innerHeight
    });

    const toLine = (values) =>
      values
        .map((value, index) => {
          const { x, y } = pointAt(value, index);
          return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
        })
        .join(' ');

    const isDrawable = width > 0 && categories.length > 1;
    const activeX = activeIndex === null ? 0 : activeIndex * stepX;

    return (
      <div
        ref={ref}
        className={`linechart ${className}`.trim()}
        onMouseLeave={() => {
          setActiveIndex(null);
          setActiveSeries(null);
        }}
        {...props}
      >
        <div className="linechart__header">
          {title && (
            <span className="linechart__status">
              <span className="linechart__dot" aria-hidden="true" />
              <span className="linechart__title">{title}</span>
            </span>
          )}
          <span className={`linechart__readout ${activeIndex !== null ? 'is-active' : ''}`}>
            {activeIndex !== null ? categories[activeIndex] : ''}
          </span>
        </div>

        <div className="linechart__plot" style={{ height }} ref={plotRef}>
          {isDrawable && (
            <svg className="linechart__svg" width={width} height={height} aria-hidden="true">
              {Array.from({ length: GRID_LINES + 1 }).map((_, index) => {
                const y = PADDING_TOP + (innerHeight / GRID_LINES) * index;
                return (
                  <line
                    key={index}
                    className="linechart__gridline"
                    x1="0"
                    x2={width}
                    y1={y}
                    y2={y}
                  />
                );
              })}

              {activeIndex !== null && (
                <line
                  className="linechart__guide"
                  x1={activeX}
                  x2={activeX}
                  y1={PADDING_TOP}
                  y2={PADDING_TOP + innerHeight}
                />
              )}

              {series.map((one, index) => {
                const dimmed = activeSeries !== null && activeSeries !== one.label;
                return (
                  <g
                    key={one.label}
                    className={`linechart__series ${dimmed ? 'is-dimmed' : ''}`}
                    style={{ '--line-color': one.color }}
                  >
                    <motion.path
                      className="linechart__line"
                      d={toLine(one.values)}
                      initial={reduced ? false : { pathLength: 0 }}
                      animate={{ pathLength: 1 }}
                      transition={
                        reduced
                          ? { duration: 0 }
                          : {
                              duration: animationDuration,
                              delay: index * animationDelayPerSeries,
                              ease: 'easeInOut'
                            }
                      }
                    />
                    {activeIndex !== null && (
                      <circle
                        className="linechart__point"
                        cx={pointAt(one.values[activeIndex], activeIndex).x}
                        cy={pointAt(one.values[activeIndex], activeIndex).y}
                        r="4.5"
                      />
                    )}
                  </g>
                );
              })}
            </svg>
          )}

          {/* Zonas de captura: una por fecha, para que el raton no tenga que
              acertarle a un punto de cuatro pixeles. Cada una va centrada en
              su punto, que no es lo mismo que repartir el ancho a partes
              iguales: el primer punto esta en el borde, no a medio hueco. */}
          <div className="linechart__hits">
            {categories.map((category, index) => {
              const from = Math.max(0, (index - 0.5) * stepX);
              const to = Math.min(width, (index + 0.5) * stepX);
              return (
                <span
                  key={category}
                  className="linechart__hit"
                  style={{ left: from, width: Math.max(to - from, 0) }}
                  onMouseEnter={() => setActiveIndex(index)}
                />
              );
            })}
          </div>

          {activeIndex !== null && isDrawable && (
            /* El globo se va al lado contrario de la guia en vez de centrarse
               sobre ella: centrado tapaba justo los puntos que se van a leer. */
            <div
              className={`linechart__tooltip ${
                activeIndex > (categories.length - 1) / 2 ? 'is-flipped' : ''
              }`}
              style={{ left: activeX }}
            >
              <span className="linechart__tooltip-title">{categories[activeIndex]}</span>
              {series.map((one) => (
                <span key={one.label} className="linechart__tooltip-row">
                  <span
                    className="linechart__tooltip-dot"
                    style={{ backgroundColor: one.color }}
                  />
                  {one.label}
                  <b>{one.values[activeIndex]}{unit}</b>
                </span>
              ))}
            </div>
          )}
        </div>

        <ul className="linechart__axis" style={{ width }}>
          {categories.map((category, index) => (
            <li
              key={category}
              className={`linechart__axis-item ${activeIndex === index ? 'is-active' : ''}`}
              style={{ left: index * stepX }}
            >
              {category}
            </li>
          ))}
        </ul>

        <ul className="linechart__legend">
          {series.map((one) => (
            <li
              key={one.label}
              className={`linechart__legend-item ${activeSeries === one.label ? 'is-active' : ''}`}
              style={{ '--line-color': one.color }}
              onMouseEnter={() => setActiveSeries(one.label)}
              onMouseLeave={() => setActiveSeries(null)}
            >
              <span className="linechart__legend-dot" aria-hidden="true" />
              {one.label}
            </li>
          ))}
        </ul>
      </div>
    );
  }
);

LineChart.displayName = 'LineChart';

export default LineChart;
export { LineChart };
